/**
 * PhoneModal Component
 * Unified modal that displays phone number with copy/call actions
 * AND outcome options (¿Qué pasó?) in a single view.
 *
 * When "No interesado" is selected, opens CancellationModal to capture
 * cancellation reasons before confirming.
 */

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  PhoneIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  XMarkIcon,
  CheckCircleIcon,
  PhoneXMarkIcon,
  ClockIcon,
  XCircleIcon,
  CalendarDaysIcon,
  DocumentTextIcon,
  PaperAirplaneIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { CancellationModal, type CancellationModalResult } from '@/shared/components/ui/CancellationModal';
import { CalendarModal } from '@/shared/components/CalendarModal';

export type OutcomeType =
  | 'contacted'
  | 'no_answer'
  | 'busy'
  | 'not_interested'
  | 'appointment'
  | 'quote_sent';

type Step = 'main' | 'contacted_sub' | 'no_answer_retry';

interface OutcomeOption {
  id: string;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  bgColor: string;
}

// Paso 1: Contactado o No contestó
const mainOptions: OutcomeOption[] = [
  {
    id: 'contacted',
    label: 'Contactado',
    description: 'Hablé con el lead',
    icon: CheckCircleIcon,
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 border-green-200 dark:border-green-800',
  },
  {
    id: 'no_answer',
    label: 'No contestó',
    description: 'Intenté pero no respondió',
    icon: PhoneXMarkIcon,
    color: 'text-amber-600 dark:text-amber-400',
    bgColor: 'bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 border-amber-200 dark:border-amber-800',
  },
];

// Paso 2A: Sub-opciones cuando SÍ contactó
const contactedSubOptions: OutcomeOption[] = [
  {
    id: 'busy',
    label: 'Llamar después',
    description: 'Programar próxima llamada',
    icon: ClockIcon,
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 border-blue-200 dark:border-blue-800',
  },
  {
    id: 'appointment',
    label: 'Cita agendada',
    description: 'Programar cita presencial',
    icon: CalendarDaysIcon,
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 border-purple-200 dark:border-purple-800',
  },
  {
    id: 'not_interested',
    label: 'No interesado',
    description: 'El lead no tiene interés',
    icon: XCircleIcon,
    color: 'text-gray-600 dark:text-gray-400',
    bgColor: 'bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 border-gray-200 dark:border-gray-700',
  },
];

export interface NotInterestedData {
  cancellation: CancellationModalResult;
  notes?: string;
}

export interface NoAnswerRetryInfo {
  attemptNumber: number;
  suggestedDate: Date;
  maxAttempts: number;
}

interface PhoneModalProps {
  open: boolean;
  onClose: () => void;
  phone: string;
  name?: string;
  context?: string;
  leadId?: string;
  onOutcomeSelect?: (outcome: OutcomeType, notes?: string) => void;
  /** Called when "No interesado" is confirmed with cancellation reasons */
  onNotInterested?: (data: NotInterestedData) => void;
  /** Called when "Cita agendada" is selected — host should open CalendarModal for meeting */
  onScheduleAppointment?: (notes?: string) => void;
  /** Called when "Llamar después" is selected — host should open CalendarModal for call */
  onScheduleCallback?: (notes?: string) => void;
  /** Retry info for "No contestó" — if provided, shows inline retry panel */
  noAnswerRetryInfo?: NoAnswerRetryInfo | null;
  /** Called when "No contestó" is clicked — host should load retry info */
  onNoAnswerClick?: () => void;
  /** Called when user confirms the retry with a date */
  onNoAnswerConfirm?: (date: Date) => void;
  /** Entity name shown in CancellationModal (defaults to name prop) */
  entityName?: string;
}

export const PhoneModal: React.FC<PhoneModalProps> = ({
  open,
  onClose,
  phone,
  name,
  context,
  leadId,
  onOutcomeSelect,
  onNotInterested,
  onScheduleAppointment,
  onScheduleCallback,
  noAnswerRetryInfo,
  onNoAnswerClick,
  onNoAnswerConfirm,
  entityName,
}) => {
  const [copied, setCopied] = useState(false);
  const [step, setStep] = useState<Step>('main');
  const [selectedOutcome, setSelectedOutcome] = useState<OutcomeType | null>(null);
  const [notes, setNotes] = useState('');
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [showRetryDatePicker, setShowRetryDatePicker] = useState(false);
  const [retryDate, setRetryDate] = useState<Date | null>(null);
  const [showCallbackDatePicker, setShowCallbackDatePicker] = useState(false);
  const [callbackDate, setCallbackDate] = useState<Date | null>(null);
  const [showAppointmentDatePicker, setShowAppointmentDatePicker] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState<Date | null>(null);

  // Sync retry date when info changes
  useEffect(() => {
    if (noAnswerRetryInfo) {
      setRetryDate(noAnswerRetryInfo.suggestedDate);
    }
  }, [noAnswerRetryInfo]);

  if (!open) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(phone);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCall = () => {
    window.open(`tel:${phone}`, '_self');
  };

  const resetState = () => {
    setStep('main');
    setSelectedOutcome(null);
    setNotes('');
    setCopied(false);
    setRetryDate(null);
    setCallbackDate(null);
    setAppointmentDate(null);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleMainOptionClick = (id: string) => {
    if (id === 'contacted') {
      setStep('contacted_sub');
      return;
    }
    if (id === 'no_answer') {
      // No contestó → mostrar panel de reintentos
      setSelectedOutcome('no_answer');
      setStep('no_answer_retry');
      if (onNoAnswerClick) onNoAnswerClick();
      return;
    }
  };

  const handleContactedSubClick = (id: string) => {
    if (id === 'appointment') {
      setSelectedOutcome('appointment');
      return;
    }
    if (id === 'busy') {
      setSelectedOutcome('busy');
      return;
    }
    if (id === 'not_interested') {
      if (onNotInterested) {
        setShowCancellationModal(true);
      } else if (onOutcomeSelect) {
        onOutcomeSelect('not_interested', undefined);
        resetState();
        onClose();
      }
      return;
    }
  };

  const handleCancellationSubmit = (data: CancellationModalResult) => {
    setShowCancellationModal(false);
    if (onNotInterested) {
      onNotInterested({ cancellation: data, notes: notes.trim() || undefined });
    }
    resetState();
    onClose();
  };

  const handleConfirmOutcome = () => {
    if (!selectedOutcome) return;

    // Caso especial: Cita agendada → delegar al host para abrir CalendarModal
    if (selectedOutcome === 'appointment' && onScheduleAppointment) {
      onScheduleAppointment(notes.trim() || undefined);
      resetState();
      onClose();
      return;
    }

    if (onOutcomeSelect) {
      onOutcomeSelect(selectedOutcome, notes.trim() || undefined);
    }
    resetState();
    onClose();
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[99999]"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-sky-50 dark:bg-sky-900/30 flex items-center justify-center">
              <PhoneIcon className="h-5 w-5 text-sky-600 dark:text-sky-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {name || 'Contacto'}
              </h3>
              {context && (
                <p className="text-xs text-gray-500 dark:text-gray-400">{context}</p>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="overflow-y-auto flex-1">
          {/* Phone Number + Actions */}
          <div className="px-6 py-5">
            <div className="text-center mb-4">
              <p className="text-2xl font-mono font-semibold text-gray-900 dark:text-white tracking-wide">
                {phone}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleCopy}
                className={`flex flex-col items-center gap-2 p-3 rounded-xl transition-all ${
                  copied
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {copied ? (
                  <CheckIcon className="h-5 w-5" />
                ) : (
                  <ClipboardDocumentIcon className="h-5 w-5" />
                )}
                <span className="text-xs font-medium">{copied ? 'Copiado' : 'Copiar'}</span>
              </button>

              <button
                onClick={handleCall}
                className="flex flex-col items-center gap-2 p-3 rounded-xl bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400 hover:bg-sky-200 dark:hover:bg-sky-900/50 transition-colors"
              >
                <PhoneIcon className="h-5 w-5" />
                <span className="text-xs font-medium">Llamar</span>
              </button>
            </div>
          </div>

          {/* Outcome Section */}
          {(onOutcomeSelect || onNotInterested) && (
            <>
              <div className="mx-6 border-t border-gray-200 dark:border-gray-800" />

              <div className="px-6 py-4">
                <div className="flex items-center gap-2 mb-3">
                  {step !== 'main' && (
                    <button
                      onClick={() => { setStep('main'); setSelectedOutcome(null); }}
                      className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                      title="Volver"
                    >
                      <svg className="w-4 h-4 text-gray-500" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                    </button>
                  )}
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {step === 'main' ? '¿Qué pasó?' : step === 'contacted_sub' ? '¿Qué sigue?' : 'No contestó'}
                  </p>
                </div>

                {/* Paso 1: Contactado o No contestó */}
                {step === 'main' && (
                  <div className="space-y-2">
                    {mainOptions.map((option) => {
                      const Icon = option.icon;
                      return (
                        <button
                          key={option.id}
                          onClick={() => handleMainOptionClick(option.id)}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${option.bgColor}`}
                        >
                          <div className={`p-1.5 rounded-lg bg-white dark:bg-gray-800 ${option.color}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 text-left">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {option.label}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {option.description}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Paso 2A: Sub-opciones de "Contactado" */}
                {step === 'contacted_sub' && !selectedOutcome && (
                  <div className="space-y-2">
                    {contactedSubOptions.map((option) => {
                      const Icon = option.icon;
                      return (
                        <button
                          key={option.id}
                          onClick={() => handleContactedSubClick(option.id)}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all ${option.bgColor}`}
                        >
                          <div className={`p-1.5 rounded-lg bg-white dark:bg-gray-800 ${option.color}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 text-left">
                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                              {option.label}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {option.description}
                            </p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Panel inline de reintentos — para "No contestó" */}
              {/* Panel inline — reintentos "No contestó" */}
              {step === 'no_answer_retry' && selectedOutcome === 'no_answer' && (() => {
                const RETRY_LABELS = ['24 horas', '48 horas', '5 días'];
                const info = noAnswerRetryInfo;
                const maxAttempts = info?.maxAttempts || 4;
                const attemptNumber = info?.attemptNumber || 1;
                const isLastAttempt = attemptNumber >= maxAttempts;
                const retryIndex = Math.min(attemptNumber - 1, RETRY_LABELS.length - 1);
                const displayDate = retryDate || info?.suggestedDate || new Date();

                return (
                  <div className="px-6 pb-4 space-y-3">
                    {/* Barra de progreso */}
                    {info ? (
                      <>
                        <div className="flex items-center gap-1.5">
                          {[1, 2, 3, 4].map((n) => (
                            <div key={n} className="flex-1 flex flex-col items-center gap-0.5">
                              <div className={`w-full h-1.5 rounded-full ${
                                n <= attemptNumber
                                  ? n >= 4 ? 'bg-red-500' : 'bg-amber-500'
                                  : 'bg-gray-200 dark:bg-gray-700'
                              }`} />
                              <span className="text-[8px] text-gray-400">{n}</span>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
                          Intento <span className="font-semibold">{attemptNumber}</span> de {maxAttempts}
                          {!isLastAttempt && ` — próximo en ${RETRY_LABELS[retryIndex] || '5 días'}`}
                        </p>

                        {isLastAttempt ? (
                          <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3">
                            <div className="flex items-start gap-2">
                              <ExclamationTriangleIcon className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-xs font-medium text-red-700 dark:text-red-400">
                                  Se agotaron los intentos de contacto.
                                </p>
                                <p className="text-xs text-red-600 dark:text-red-300 mt-0.5">
                                  Considera descartar o intentar por otro canal.
                                </p>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="text-[10px] text-amber-700 dark:text-amber-300 font-medium">
                                    Próximo intento
                                  </p>
                                  <p className="text-sm font-semibold text-gray-900 dark:text-white capitalize">
                                    {displayDate.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
                                  </p>
                                </div>
                                <button
                                  onClick={() => setShowRetryDatePicker(true)}
                                  className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-medium text-amber-700 dark:text-amber-300 bg-white dark:bg-gray-800 border border-amber-300 dark:border-amber-700 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/30 transition-colors"
                                >
                                  <CalendarDaysIcon className="h-3 w-3" />
                                  Cambiar
                                </button>
                              </div>
                            </div>

                            <button
                              onClick={() => {
                                if (onNoAnswerConfirm && retryDate) {
                                  onNoAnswerConfirm(retryDate);
                                  resetState();
                                  onClose();
                                }
                              }}
                              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 transition-colors"
                            >
                              <CheckIcon className="w-4 h-4" />
                              Agendar reintento
                            </button>
                          </>
                        )}
                      </>
                    ) : (
                      <div className="flex items-center justify-center py-3">
                        <span className="h-4 w-4 border-2 border-gray-300 border-t-amber-600 rounded-full animate-spin mr-2" />
                        <span className="text-xs text-gray-500">Cargando intentos...</span>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Panel inline — para "Llamar después" */}
              {step === 'contacted_sub' && selectedOutcome === 'busy' && (
                <div className="px-6 pb-4 space-y-3">
                  <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[10px] text-blue-700 dark:text-blue-300 font-medium">
                          Fecha de llamada
                        </p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white capitalize">
                          {callbackDate
                            ? callbackDate.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })
                            : 'Seleccionar fecha'
                          }
                        </p>
                      </div>
                      <button
                        onClick={() => setShowCallbackDatePicker(true)}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-medium text-blue-700 dark:text-blue-300 bg-white dark:bg-gray-800 border border-blue-300 dark:border-blue-700 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                      >
                        <CalendarDaysIcon className="h-3 w-3" />
                        {callbackDate ? 'Cambiar' : 'Elegir'}
                      </button>
                    </div>
                  </div>

                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Notas adicionales (opcional)..."
                    rows={2}
                    className="w-full resize-none rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  />

                  <button
                    onClick={() => {
                      if (callbackDate && onScheduleCallback) {
                        onScheduleCallback(callbackDate.toISOString());
                        resetState();
                        onClose();
                      }
                    }}
                    disabled={!callbackDate}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <CheckIcon className="w-4 h-4" />
                    Programar llamada
                  </button>
                </div>
              )}

              {/* Panel inline — para "Cita agendada" */}
              {step === 'contacted_sub' && selectedOutcome === 'appointment' && (
                <div className="px-6 pb-4 space-y-3">
                  <div className="rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[10px] text-purple-700 dark:text-purple-300 font-medium">
                          Fecha de la cita
                        </p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-white capitalize">
                          {appointmentDate
                            ? appointmentDate.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })
                            : 'Seleccionar fecha'
                          }
                        </p>
                      </div>
                      <button
                        onClick={() => setShowAppointmentDatePicker(true)}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-medium text-purple-700 dark:text-purple-300 bg-white dark:bg-gray-800 border border-purple-300 dark:border-purple-700 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-colors"
                      >
                        <CalendarDaysIcon className="h-3 w-3" />
                        {appointmentDate ? 'Cambiar' : 'Elegir'}
                      </button>
                    </div>
                  </div>

                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Notas adicionales (opcional)..."
                    rows={2}
                    className="w-full resize-none rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 focus:outline-none"
                  />

                  <button
                    onClick={() => {
                      if (appointmentDate && onScheduleAppointment) {
                        onScheduleAppointment(appointmentDate.toISOString());
                        resetState();
                        onClose();
                      }
                    }}
                    disabled={!appointmentDate}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-purple-600 text-white text-sm font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <CheckIcon className="w-4 h-4" />
                    Agendar cita
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-800 flex-shrink-0">
          <button
            onClick={handleClose}
            className="w-full py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 font-medium transition-colors"
          >
            Cancelar
          </button>
        </div>

        {/* CancellationModal for "No interesado" */}
        <CancellationModal
          isOpen={showCancellationModal}
          onClose={() => setShowCancellationModal(false)}
          onSubmit={handleCancellationSubmit}
          entityName={entityName || name}
          viewerContext="advisor"
          confirmLabel="Confirmar cancelación"
        />

        {/* CalendarModal for appointment date (Cita agendada) */}
        {showAppointmentDatePicker && (
          <CalendarModal
            open
            onClose={() => setShowAppointmentDatePicker(false)}
            lead={leadId ? { id: leadId, name: name || '' } : undefined}
            title={`Agendar cita — ${name || 'Lead'}`}
            minDate={new Date().toISOString().slice(0, 10)}
            initialDate={appointmentDate || undefined}
            onDateSelect={(date) => {
              setAppointmentDate(date);
              setShowAppointmentDatePicker(false);
            }}
          />
        )}

        {/* CalendarModal for callback date (Llamar después) */}
        {showCallbackDatePicker && (
          <CalendarModal
            open
            onClose={() => setShowCallbackDatePicker(false)}
            lead={leadId ? { id: leadId, name: name || '' } : undefined}
            title={`Programar llamada — ${name || 'Lead'}`}
            minDate={new Date().toISOString().slice(0, 10)}
            initialDate={callbackDate || undefined}
            onDateSelect={(date) => {
              setCallbackDate(date);
              setShowCallbackDatePicker(false);
            }}
          />
        )}

        {/* CalendarModal for retry date change */}
        {showRetryDatePicker && (
          <CalendarModal
            open
            onClose={() => setShowRetryDatePicker(false)}
            lead={leadId ? { id: leadId, name: name || '' } : undefined}
            title={`Seleccionar fecha — ${name || 'Lead'}`}
            minDate={new Date().toISOString().slice(0, 10)}
            initialDate={retryDate || undefined}
            onDateSelect={(date) => {
              setRetryDate(date);
              setShowRetryDatePicker(false);
            }}
          />
        )}
      </div>
    </div>,
    document.body
  );
};
